import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _first from 'lodash/first';
import _isEmpty from 'lodash/isEmpty';
import _reduce from 'lodash/reduce';

import { ShopifyProduct } from '../components/common/ShopifyBuyButton';
import {
  ArtworkMetadata,
  BoothAuthority,
  BoothData,
  MuseumTicketData,
} from '../interfaces/museum';
import { findTicketByBooth } from './ticket';

type BoothWithArtwork = {
  booth?: BoothData;
  artwork?: ArtworkMetadata;
};

function createBoothAuthority(
  booths?: BoothData[],
  museumTickets?: MuseumTicketData[],
): BoothAuthority {
  if (_isEmpty(booths)) {
    return {};
  }

  const myTickets = _filter(museumTickets, [
    'isOwned',
    true,
  ]) as MuseumTicketData[];

  return _reduce(
    booths,
    (result, b) => {
      const datoId = b.id;

      const isAccessible = b.needTicket
        ? !!findTicketByBooth(myTickets, datoId)
        : true;
      result[b.id] = isAccessible;
      return result;
    },
    {} as Record<number, boolean>,
  );
}

function findBoothById(
  values: BoothData[],
  boothId: string,
): BoothData | undefined {
  if (_isEmpty(values)) {
    return undefined;
  }

  return _find(values, ['id', boothId]);
}

function findArtworkById(
  values: ArtworkMetadata[] | BoothData[],
  artworkId: string,
): BoothWithArtwork | undefined {
  if (_isEmpty(values)) {
    return undefined;
  }

  const booths = values as BoothData[];
  if (booths && _first(booths)?.artworks) {
    const booth = _find(booths, ({ artworks }) => {
      return !!_find(artworks, ['id', artworkId]);
    });
    if (!booth) {
      return undefined;
    }

    const artwork = _find(booth?.artworks, ['id', artworkId]);
    return {
      booth,
      artwork,
    };
  }

  const artworks = values as ArtworkMetadata[];
  return {
    artwork: _find(artworks, ['id', artworkId]),
  };
}

function hasShopifyProduct(booth?: BoothData): boolean {
  if (!booth) {
    return false;
  }

  const { artworks } = booth;
  const shopifyProductItems = _filter(
    artworks,
    ({ shopifyProduct, shopifyCollectionId }) => {
      return shopifyProduct !== '' || shopifyCollectionId !== '';
    },
  );
  return !_isEmpty(shopifyProductItems);
}

function getShopifyProductData(
  artwork?: ArtworkMetadata,
): ShopifyProduct | null {
  if (!artwork || !artwork.shopifyProduct) {
    return null;
  }

  return JSON.parse(artwork.shopifyProduct);
}

function findBoothsByOpen(booths: BoothData[]): BoothData[] {
  const openBooths = _reduce(
    booths,
    (result, booth: BoothData) => {
      if (!booth.isOpen) {
        return result;
      }

      const openArtworks = findArtworksByOpen(booth.artworks);
      const newBooth: BoothData = {
        ...booth,
        artworks: openArtworks as ArtworkMetadata[],
      };

      result.push(newBooth);
      return result;
    },
    [] as BoothData[],
  );

  return openBooths;
}

function findArtworksByOpen(artworks: ArtworkMetadata[]): ArtworkMetadata[] {
  return _filter(artworks, { isOpen: true });
}

export {
  createBoothAuthority,
  findArtworkById,
  findBoothById,
  findBoothsByOpen,
  getShopifyProductData,
  hasShopifyProduct,
};
